import { z } from 'zod';

/**
 * Mirrors apps/api/src/modules/catalog/http/product.dto.ts and
 * product-variant.dto.ts field-for-field — same names, same bounds. A
 * validation error surfaces before the round trip, never drifts from what
 * the server actually enforces without both files being read side by side.
 */

const SLUG_PATTERN = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
const SLUG_MESSAGE =
  'Le slug doit être en minuscules, chiffres et tirets simples, 2 à 63 caractères.';

/** zod's default messages are technical English (« String must contain at least 2 character(s) »): every bound a merchant can hit states its own. */
const maxChars = (max: number): string => `${String(max)} caractères au maximum.`;

const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(2, SLUG_MESSAGE)
  .max(63, SLUG_MESSAGE)
  .regex(SLUG_PATTERN, SLUG_MESSAGE);

const skuSchema = z.string().trim().min(1, 'Le SKU est requis.').max(100, maxChars(100));

/** An empty number input reaches here as `NaN`, which zod reports as an invalid type (« Expected number, received nan »). */
const moneySchema = z
  .number({ invalid_type_error: 'Indiquez un montant valide.' })
  .nonnegative('Le montant ne peut pas être négatif.')
  .max(999_999_999, 'Montant trop élevé.');

/**
 * `register(name, { valueAsNumber: true })` on an EMPTY number input
 * reports `NaN`, not `undefined` — `Number('')` is `NaN`. Left as-is, an
 * optional money field the merchant simply leaves blank (e.g. "prix
 * barré") would fail validation as "expected number, received NaN" instead
 * of being treated as "not set". This preprocesses that one case before
 * the real bound checks run.
 */
const optionalMoneySchema = z.preprocess(
  (value) => (typeof value === 'number' && Number.isNaN(value) ? null : value),
  moneySchema.nullable().optional(),
);

/**
 * A native `<select>` with no selection reports `""` (ui-kit's `Select`
 * renders that as its placeholder option) — not `undefined`, which is what
 * `productInputSchema` on the server actually expects for "no category".
 * This accepts the empty string a real unselected select produces and
 * normalises it to `null` before validation runs, so leaving the category
 * unset never surfaces as a spurious "invalid uuid" error.
 */
const optionalCategoryId = z
  .union([z.literal(''), z.string().trim().uuid()])
  .nullable()
  .optional()
  .transform((value) => (value === '' || value === undefined ? null : value));

export const productFormSchema = z
  .object({
    categoryId: optionalCategoryId,

    name: z.string().trim().min(1, 'Le nom est requis.').max(200, maxChars(200)),
    slug: slugSchema,
    shortDescription: z.string().trim().max(500, maxChars(500)).nullable().optional(),
    longDescription: z.string().trim().max(20_000, maxChars(20_000)).nullable().optional(),

    sku: skuSchema,
    barcode: z.string().trim().max(100, maxChars(100)).nullable().optional(),

    price: moneySchema,
    compareAtPrice: optionalMoneySchema,
    purchasePrice: optionalMoneySchema,
    vatRate: z.number().min(0).max(100).nullable().optional(),

    weight: z.number().nonnegative().max(1_000_000).nullable().optional(),
    brand: z.string().trim().max(200, maxChars(200)).nullable().optional(),

    status: z.enum(['draft', 'active', 'inactive']).optional(),

    seoTitle: z.string().trim().max(200, maxChars(200)).nullable().optional(),
    seoDescription: z.string().trim().max(500, maxChars(500)).nullable().optional(),
  })
  .superRefine((value, ctx) => {
    // Same rule as the API: a « prix barré » at or below the price would show a discount that does not exist.
    if (
      value.compareAtPrice !== null &&
      value.compareAtPrice !== undefined &&
      value.compareAtPrice <= value.price
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['compareAtPrice'],
        message: 'Le prix barré doit être supérieur au prix.',
      });
    }
  });
export type ProductFormValues = z.infer<typeof productFormSchema>;

export const variantFormSchema = z.object({
  sku: skuSchema,
  /** Free-form key/value pairs (e.g. couleur, taille) — DEV §6.2's open bag, no fixed keys. */
  attributes: z.array(
    z.object({ key: z.string().trim().min(1).max(100), value: z.string().trim().min(1).max(200) }),
  ),
  priceOverride: optionalMoneySchema,
});
export type VariantFormValues = z.infer<typeof variantFormSchema>;

/**
 * Same `valueAsNumber` NaN-on-empty behaviour as `optionalMoneySchema`
 * above, but leaving the field blank here means "leave the stored
 * threshold untouched" (`undefined`, per `inventory.dto.ts`'s own
 * distinction), never "clear it" (`null`) — this form offers no control
 * for that second, different action.
 */
const optionalIntThreshold = z.preprocess(
  (value) => (typeof value === 'number' && Number.isNaN(value) ? undefined : value),
  z.number().int().min(0).optional(),
);

/**
 * A plain text input left empty reports `""`, not `undefined` — but the
 * server's own schema requires `.min(1)` on a stated reason (an empty
 * string is not "no reason", it is an invalid one). Empty is normalised to
 * `undefined` here for the identical reason `optionalCategoryId` normalises
 * an unselected `<select>`'s `""`.
 */
const optionalReason = z
  .string()
  .trim()
  .max(500)
  .optional()
  .transform((value) => (value === '' || value === undefined ? undefined : value));

export const inventoryAdjustFormSchema = z.object({
  delta: z
    .number()
    .int('La quantité doit être un nombre entier.')
    .refine((value) => value !== 0, { message: "L'ajustement ne peut pas être nul." }),
  reason: optionalReason,
  quantityMinThreshold: optionalIntThreshold,
});
export type InventoryAdjustFormValues = z.infer<typeof inventoryAdjustFormSchema>;

/**
 * Variant-improvements lot — mirrors
 * apps/api/src/modules/catalog/http/product-option.dto.ts field-for-field.
 * Each option/value is its own resource with its own endpoint
 * (`OptionBuilder.tsx` calls one mutation per add/rename/delete), unlike
 * `variantFormSchema` above which submits everything in one POST.
 */
const optionNameSchema = z.string().trim().min(1).max(100);
const optionValueTextSchema = z.string().trim().min(1).max(100);

export const optionFormSchema = z.object({
  name: optionNameSchema,
  displayType: z.enum(['button', 'color_swatch']),
});
export type OptionFormValues = z.infer<typeof optionFormSchema>;

export const optionValueFormSchema = z.object({
  value: optionValueTextSchema,
  /**
   * Three distinct intents, matching the API's own PATCH semantics
   * (`ProductOptionRepository.updateValue()`: `input.swatchHex ===
   * undefined ? existing.swatch_hex : input.swatchHex`): omitted
   * entirely -> leave untouched, `''` -> explicit clear (`null`), a
   * real hex string -> set it. `OptionBuilder.tsx`'s inline text-only
   * editing never sets this key at all, so a rename never accidentally
   * clears a swatch colour it doesn't manage.
   */
  swatchHex: z
    .union([
      z.literal(''),
      z
        .string()
        .trim()
        .regex(/^#[0-9a-fA-F]{6}$/),
    ])
    .nullable()
    .optional()
    .transform((v) => (v === '' ? null : v)),
});
export type OptionValueFormValues = z.infer<typeof optionValueFormSchema>;

/**
 * Bulk-edit form (`BulkEditGrid.tsx`) — mirrors
 * `bulkUpdateVariantsInputSchema` (product-option.dto.ts) minus the
 * `selector`, which the grid's own row-selection state supplies directly,
 * never through this form. `priceMode: 'none'` means "leave price
 * untouched" — the API schema itself has no such literal; the grid
 * translates `'none'` to omitting `priceAdjustment` entirely before POSTing.
 */
/**
 * Same empty-number-input-reports-NaN preprocessing as
 * `optionalIntThreshold` above, but unbounded (a stock delta is
 * legitimately negative — "retirer du stock") where that one floors at 0.
 */
const optionalIntDelta = z.preprocess(
  (value) => (typeof value === 'number' && Number.isNaN(value) ? undefined : value),
  z.number().int().optional(),
);

/** Same NaN-on-empty preprocessing — blank becomes `undefined` so the `.refine()` below reports its own "Indiquez un montant." rather than zod's generic number error. */
const optionalPriceAmount = z.preprocess(
  (value) => (typeof value === 'number' && Number.isNaN(value) ? undefined : value),
  z.number().optional(),
);

export const bulkEditFormSchema = z
  .object({
    priceMode: z.enum(['none', 'relative', 'absolute']),
    priceAmount: optionalPriceAmount,
    stockDelta: optionalIntDelta,
    quantityMinThreshold: optionalIntThreshold.nullable(),
    isActive: z.enum(['unchanged', 'activate', 'deactivate']),
    reason: optionalReason,
  })
  .refine(
    (value) =>
      value.priceMode === 'none' ||
      (value.priceAmount !== undefined && !Number.isNaN(value.priceAmount)),
    { message: 'Indiquez un montant.', path: ['priceAmount'] },
  )
  .refine(
    (value) =>
      value.priceMode !== 'none' ||
      (value.stockDelta !== undefined && value.stockDelta !== 0) ||
      value.quantityMinThreshold !== undefined ||
      value.isActive !== 'unchanged',
    { message: 'Choisissez au moins une modification à appliquer.', path: ['priceMode'] },
  );
export type BulkEditFormValues = z.infer<typeof bulkEditFormSchema>;
